import { NodeEventsTypes, sdkComponents, SDKComponentsTypes } from 'src/service/scene/enums';
import { EventEmitter, ListenerFn } from 'eventemitter3';
import { MpSdk } from 'src/types';
import { DeviceManager } from 'src/utils';
import ThreeInstance from 'src/sdk-components/ThreeInstance';
import { shadowComponentType } from 'src/sdk-components';

class Scene {
  scene: any;

  on: (type: NodeEventsTypes, callback: ListenerFn) => void;

  once: (type: NodeEventsTypes, callback: ListenerFn) => void;

  off: (type: NodeEventsTypes, callback?: ListenerFn) => void;

  private eventCallbacks = new Map<NodeEventsTypes, ListenerFn>();

  public outlinedMeshes: any[] = [];

  emit: (type: NodeEventsTypes, data?: unknown) => void;

  readonly mpSDK: any;

  constructor(mpSDK: MpSdk) {
    this.mpSDK = mpSDK;
    this.scene = mpSDK.Scene;

    this.registrations = this.registrations.bind(this);

    const interaction = new EventEmitter();

    this.once = function (type: NodeEventsTypes, callback: ListenerFn) {
      if (this.eventCallbacks?.size) {
        interaction.removeListener(type, this.eventCallbacks.get(type));
      }
      interaction.on(type, callback);
      this.eventCallbacks.set(type, callback);
    };

    this.on = function (type: NodeEventsTypes, callback: ListenerFn) {
      interaction.on(type, callback);
      this.eventCallbacks.set(type, callback);
    };

    this.off = function (type: NodeEventsTypes, callback?: ListenerFn) {
      interaction.off(type, callback);
    };

    this.emit = function (type: NodeEventsTypes, data?: unknown) {
      interaction.emit(type, data);
    };
  }

  async registrations(register: SDKComponentsTypes | SDKComponentsTypes[]) {
    if (Array.isArray(register)) {
      await Promise.all(
        register.map(async (type) => {
          return this.scene.register(
            type,
            sdkComponents.find((item) => item.type === type)?.component
          );
        })
      );
    } else {
      await this.scene.register(
        register,
        sdkComponents.find((item) => item.type === register)?.component
      );
    }
  }

  async configureVSScene(data: {
    register: SDKComponentsTypes | SDKComponentsTypes[];
    vsSettings: any;
    floors: any;
    vsObjects: any;
  }) {
    const { vsSettings, floors, vsObjects } = data;
    await this.registrations(data.register);
    // CachedGeometry.TempInit();

    if (DeviceManager.isMobile()) {
      await this.scene.configure((renderer: any) => {
        renderer.gammaOutput = true;
      });

      const lightsNode = await this.scene.createNode();
      lightsNode.addComponent('mp.pointLight', {
        position: {
          x: vsSettings.lightAngle ? 10 : 0,
          y: vsSettings.lightHeight,
          z: 0
        },
        intensity: vsSettings.pointLight,
        castShadow: false
      });
      lightsNode.addComponent('mp.ambientLight', {
        position: {
          x: 0,
          y: 5,
          z: 0
        },
        intensity: vsSettings.ambientLight
      });
      lightsNode.start();
    } else {
      const THREE = await ThreeInstance.get();
      await this.scene.configure((renderer: any, three: any) => {
        renderer.physicallyCorrectLights = true;
        renderer.shadowMap.enabled = true;
        renderer.shadowMap.bias = 0.0001;
        renderer.shadowMap.type = three.PCFSoftShadowMap;
      });
      const lightsNode = await this.scene.createNode();
      const pointLightComponent = lightsNode.addComponent('mp.pointLight', {
        position: {
          x: vsSettings.lightAngle ? 10 : 0,
          y: vsSettings.lightHeight,
          z: 0
        },
        intensity: vsSettings.pointLight,
        castShadow: true
      });
      lightsNode.addComponent('mp.ambientLight', {
        position: {
          x: 0,
          y: vsSettings.lightHeight,
          z: 0
        },
        intensity: vsSettings.ambientLight
      });
      lightsNode.start();

      pointLightComponent.light.angle = Math.PI * 0.2;
      pointLightComponent.light.shadow.bias = -0.000222;
      pointLightComponent.light.shadow.radius = 3;
      pointLightComponent.light.shadow.mapSize.width = 4096; // default is 512
      pointLightComponent.light.shadow.mapSize.height = 4096; // default is 512

      lightsNode.obj3D.rotation.set(
        0,
        vsSettings.lightAngle ? THREE.MathUtils.degToRad(vsSettings.lightAngle) : 0,
        0
      );

      const hasFloorRenderingOff = floors.some((i: any) => !i.active);
      if ((!hasFloorRenderingOff || floors?.length === 1) && vsObjects?.length) {
        const shadowNode = await this.scene.createNode();
        shadowNode.addComponent(shadowComponentType, {
          opacity: vsSettings.shadowIntensity
        });

        shadowNode.start();
      }

      // const [model, component] = await new TransformControls({} as any).create();

      this.mpSDK.Pointer.intersection.subscribe(
        this.emit(NodeEventsTypes.HANDLE_MODEL_INTERSECTION)
      );
    }

    return { isVSReady: true };
  }
}

export default Scene;
